import { useMutation, useQueryClient } from '@tanstack/react-query';
import { addUser } from '../api/userService';
import { toast } from '../utils/toasts';

/**
 * Hook para crear usuario
 */
export function useCreateUser() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: addUser,
    onMutate: async (newUser) => {
      // Cancelar queries en curso para evitar que sobrescriban el optimistic update
      await queryClient.cancelQueries({ queryKey: ['users'] });
      
      // Snapshot del valor anterior
      const previousUsers = queryClient.getQueryData({ queryKey: ['users'] });
      
      // Optimistic update
      queryClient.setQueryData({ queryKey: ['users'] }, (old) => {
        if (!old?.data?.results) return old;
        return {
          ...old,
          data: {
            ...old.data,
            results: [...old.data.results, { ...newUser, id: Date.now() }],
            totalResults: old.data.totalResults + 1,
          },
        };
      });
      
      return { previousUsers };
    },
    onSuccess: (data) => {
      toast('success', 'Usuario creado exitosamente');
      // Invalidar queries específicas
      queryClient.invalidateQueries({ queryKey: ['users'] });
      queryClient.invalidateQueries({ queryKey: ['user'] });
    },
    onError: (error, newUser, context) => {
      const errorMessage = error?.response?.data?.message || error?.message || 'Error al crear usuario';
      toast('error', errorMessage);
      console.error('Error creating user:', error);
      
      // Revertir optimistic update en caso de error
      if (context?.previousUsers) {
        queryClient.setQueryData({ queryKey: ['users'] }, context.previousUsers);
      }
    },
    onSettled: () => {
      // Siempre refetch para asegurar sincronización
      queryClient.invalidateQueries({ queryKey: ['users'] });
    },
    retry: 1,
    retryDelay: 1000,
  });
} 